import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackDivider,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MdBlock, MdDelete } from "react-icons/md";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Loader } from "../../components/Loader";
import { TemporaryBlockForm } from "../../forms";
import {
  AccountDocument,
  AccountRole,
  AccountType,
  ClientProfile,
  DoctorProfile,
  TemporaryBlockagesDocument,
  useAccountQuery,
  useDeleteSubscriptionsThatNeedReviewMutation,
  useTemporaryBlockagesQuery,
  useTemporaryBlockMutation,
} from "../../graphql";
import { useAuthToken } from "../../hooks";
import { formatDateTime } from "../../utils";

const DeleteAccount = () => (
  <Alert
    status="error"
    variant="subtle"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    height="200px"
  >
    <AlertIcon boxSize="40px" mr={0} />
    <AlertTitle mt={4} mb={1} fontSize="lg">
      Jeni duke fshirë abonimin e klientit!
    </AlertTitle>
    <AlertDescription maxWidth="sm">
      Duke shtypur butonin "Fshi abonimin" ju po kryeni nje veprim të
      pakthyeshëm. Jeni të sigurtë që doni të vazhdoni?
    </AlertDescription>
  </Alert>
);

const ClientOptions = ({ account }) => {
  const { role } = useAuthToken();
  const { t } = useTranslation();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [temporaryBlock, { loading: loadingTemporaryBlock }] =
    useTemporaryBlockMutation();

  const [deleteSubscription, { loading: loadingDelete }] =
    useDeleteSubscriptionsThatNeedReviewMutation();

  return (
    <>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("patients.temporaryBlock.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <TemporaryBlockForm
              onSubmit={(data) => {
                temporaryBlock({
                  variables: {
                    hours: parseInt(data.hours),
                    reason: data.reason,
                    id: account.id,
                  },
                  refetchQueries: [
                    {
                      query: TemporaryBlockagesDocument,
                      variables: {
                        id: account.id,
                      },
                    },
                  ],
                });

                onClose();
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        closeOnOverlayClick={true}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <DeleteAccount />
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Jo, sigurisht
            </Button>
            <Button
              colorScheme="red"
              onClick={async () => {
                await deleteSubscription({
                  variables: {
                    id: (account.profile as ClientProfile).subscription.id,
                  },
                  refetchQueries: [
                    {
                      query: AccountDocument,
                      variables: {
                        id: account.id,
                      },
                    },
                  ],
                });

                onClose();
              }}
              leftIcon={<MdDelete />}
            >
              Po, dua ta fshij!
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ButtonGroup size="sm" variant="outline" spacing={2}>
        <Button
          colorScheme="red"
          leftIcon={<MdBlock />}
          onClick={onOpen}
          isLoading={loadingTemporaryBlock}
        >
          {t("patients.temporaryBlock.title")}
        </Button>
        {/* <Button colorScheme="blue" leftIcon={<MdNotifications />}>
          Linjë e lirë
        </Button> */}
        {role === AccountRole.Admin &&
        (account.profile as ClientProfile)?.subscription?.id ? (
          <Button
            colorScheme="red"
            leftIcon={<MdDelete />}
            onClick={onDeleteOpen}
            isLoading={loadingDelete}
          >
            Fshi abonimin
          </Button>
        ) : null}
      </ButtonGroup>
    </>
  );
};

const ClientTable = ({ account }) => {
  const { data: blockLog, loading: loadingTemporaryBlockages } =
    useTemporaryBlockagesQuery({
      variables: {
        id: account.id,
      },
    });
  const { t } = useTranslation();

  return (
    <>
      <Center>
        <Stack>
          <Center>
            <VStack>
              <Avatar
                center
                size="lg"
                name={`${account.profile.name} ${account.profile.lastname}`}
                src={account.profileImage}
              />
            </VStack>
          </Center>

          <Stack>
            <Center>
              <Text fontSize="xl">
                {account.profile.name} {account.profile.lastname}
              </Text>
            </Center>
            <Center>
              <Text fontSize="sm">{account.role}</Text>
            </Center>
            <Center>
              <ClientOptions account={account} />
            </Center>
          </Stack>
        </Stack>
      </Center>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th isNumeric width="40%"></Th>
            <Th width="50%"></Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td isNumeric>{t("login.email")}</Td>
            <Td>{account.email}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.mobileNumber")}</Td>
            <Td>{account.phoneNumber}</Td>
          </Tr>

          <Tr>
            <Td isNumeric>{t("login.birthday")}</Td>
            <Td>{(account.profile as ClientProfile).birthday}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.sex")}</Td>
            <Td>{(account.profile as ClientProfile).sex}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.language")}</Td>
            <Td>{account.language.name}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.firstRegistration")}</Td>
            <Td>{account.createdAt}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.accountStatus")}</Td>
            <Td>{account.accountStatus}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.subscriptionStatus")}</Td>
            <Td>{account.subscriptionStatus}</Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.tier")}</Td>
            <Td>
              {(account.profile as ClientProfile).subscription?.tier.name}
            </Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.startDate")}</Td>
            <Td>
              {(account.profile as ClientProfile).subscription?.startDate}
            </Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.remainingDays")}</Td>
            <Td>
              {(account.profile as ClientProfile).subscription?.remainingDays}
            </Td>
          </Tr>
          <Tr>
            <Td isNumeric>{t("login.blockLog")}</Td>
            <Td>
              <VStack
                divider={<StackDivider borderColor="gray.200" />}
                spacing={2}
                align="stretch"
              >
                {blockLog?.temporaryBlockages.length
                  ? blockLog?.temporaryBlockages?.map((item) => {
                      return (
                        <Box>
                          <AccountAvatar
                            account={item.author}
                            size="xs"
                            role={false}
                          />
                          <Text mt={2}>
                            {item.reason || "Pa arsye"}
                            <small>
                              {" "}
                              (deri më: {formatDateTime(item.validUntil)})
                            </small>
                          </Text>
                        </Box>
                      );
                    })
                  : "-"}
              </VStack>
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </>
  );
};

const DoctorTable = ({ account }) => (
  <Table variant="simple">
    <Thead>
      <Tr>
        <Th isNumeric width="40%"></Th>
        <Th width="50%"></Th>
      </Tr>
    </Thead>
    <Tbody>
      <Tr>
        <Td isNumeric>Emri</Td>
        <Td>{account.profile.name}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Mbiemri</Td>
        <Td>{account.profile.lastname}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>E-mail</Td>
        <Td>{account.email}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Telefoni</Td>
        <Td>{account.phoneNumber}</Td>
      </Tr>

      <Tr>
        <Td isNumeric>Datëlindja</Td>
        <Td>{(account.profile as DoctorProfile).birthday}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Gjinia</Td>
        <Td>{(account.profile as DoctorProfile).sex}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Gjuha</Td>
        <Td>{account.language.name}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Rregjistrimi i parë</Td>
        <Td>{account.createdAt}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Statusi i llogarisë</Td>
        <Td>{account.accountStatus}</Td>
      </Tr>
    </Tbody>
  </Table>
);

const SystemUserTable = ({ account }) => (
  <Table variant="simple">
    <Thead>
      <Tr>
        <Th isNumeric width="40%"></Th>
        <Th width="50%"></Th>
      </Tr>
    </Thead>
    <Tbody>
      <Tr>
        <Td isNumeric>Emri</Td>
        <Td>{account.profile.name}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Mbiemri</Td>
        <Td>{account.profile.lastname}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>E-mail</Td>
        <Td>{account.email}</Td>
      </Tr>
      <Tr>
        <Td isNumeric>Telefoni</Td>
        <Td>{account.phoneNumber}</Td>
      </Tr>
    </Tbody>
  </Table>
);

export const Profile: React.FC<{ clientId: number; silent?: boolean }> = ({
  clientId,
  silent,
}) => {
  const { data, loading } = useAccountQuery({
    variables: {
      id: clientId,
    },
  });

  if (!data || loading) {
    return <Loader />;
  }

  const account = data.account;

  const accountType = account.profile.__typename;

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      p={2}
      background="white"
    >
      {accountType === AccountType.ClientProfile ? (
        <ClientTable account={account} />
      ) : accountType === AccountType.DoctorProfile ? (
        <DoctorTable account={account} />
      ) : accountType === AccountType.SystemUserProfile ? (
        <SystemUserTable account={account} />
      ) : null}
    </Box>
  );
};
